/**
 * useAuth Hook
 * Manages authentication state and operations
 */

import { useState, useCallback } from 'react';
import { requestOtp as requestOtpUseCase } from '../domain/useCases/Auth/requestOtp.js';
import { verifyUserOtp } from '../domain/useCases/Auth/verifyUserOtp.js';
import { loginUser } from '../domain/useCases/Auth/loginUser.js';
import { registerNewUser } from '../domain/useCases/Auth/registerNewUser.js';
import { logoutUser } from '../domain/useCases/Auth/logoutUser.js';
import { updateProfile } from '../data/repositories/authRepository.js';
import { storageService } from '../domain/services/StorageService.js';

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendOtp = useCallback(async (phone) => {
    setLoading(true);
    setError(null);
    try {
      const result = await requestOtpUseCase(phone);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (phone, otp) => {
    setLoading(true);
    setError(null);
    try {
      const result = await verifyUserOtp(phone, otp);
      if (result.token || result.accessToken) {
        const token = result.token || result.accessToken;
        storageService.setAuthToken(token);
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithPassword = useCallback(async (phone, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await loginUser(phone, password);
      if (result.token || result.accessToken) {
        const token = result.token || result.accessToken;
        storageService.setAuthToken(token);
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const registerUser = useCallback(async (phone, name, email) => {
    setLoading(true);
    setError(null);
    try {
      return await registerNewUser(phone, name, email);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await logoutUser();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      storageService.clearAuth();
      setLoading(false);
    }
  }, []);

  const handleUpdateProfile = useCallback(async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await updateProfile(profileData);
      // Update local user data representation
      const userData = storageService.getUserData() || {};
      storageService.setUserData({ ...userData, ...result });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    sendOtp,
    verifyOtp,
    loginWithPassword,
    registerUser,
    logout,
    handleUpdateProfile,
  };
};
